/**
 * Hono RPC Client for Compass BFF
 *
 * Type-safe API client using Hono's RPC client (`hc`).
 * The AppType is imported from compass-bff for full type inference.
 */

import { hc } from "hono/client";
import type { AppType } from "@curie/compass-bff";
import { getToken } from "@/hooks/useAuth";

// API base URL - defaults to local development server
const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

/**
 * Custom fetch wrapper that adds authentication headers
 * when a token is available.
 */
const authFetch: typeof fetch = async (input, init) => {
  const token = getToken();

  // Create new headers, preserving any existing ones
  const headers = new Headers(init?.headers);

  // Add auth header if token is available
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(input, {
    ...init,
    headers,
  });
};

/**
 * Type-safe Hono RPC client instance
 * Provides full type inference for all API routes
 * Automatically includes auth token when available
 */
export const api = hc<AppType>(API_BASE_URL, {
  fetch: authFetch,
});

/**
 * Unauthenticated API client instance
 * Use this for endpoints that don't require authentication
 */
export const publicApi = hc<AppType>(API_BASE_URL);

/**
 * API Error class for handling fetch errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public requestId?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Helper to unwrap API responses with error handling
 * Extracts data from the standard response wrapper and handles errors
 */
export async function unwrapResponse<T>(
  response: Response
): Promise<T> {
  if (!response.ok) {
    const requestId = response.headers.get("X-Request-Id") ?? undefined;

    // Handle 401 Unauthorized specifically
    if (response.status === 401) {
      throw new ApiError(
        "Authentication required",
        response.status,
        requestId
      );
    }

    throw new ApiError(
      `API request failed: ${response.statusText}`,
      response.status,
      requestId
    );
  }

  const json = await response.json();

  // Handle standard API response format { success, data, meta }
  if (typeof json === "object" && json !== null && "success" in json) {
    if (!json.success) {
      throw new ApiError(
        json.error?.message ?? "Unknown API error",
        response.status,
        json.meta?.requestId
      );
    }
    return json.data as T;
  }

  // Return raw response for non-standard responses
  return json as T;
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
